import type { IGovernanceAction } from '@src/models/types';
import { selectProtocolParams } from '@src/store/config/slice';
import { useAppSelector } from '@src/store/hooks';
import {
  filterOutNullParams,
  filterUpdatableProtocolParams,
  mapArrayToObjectByKeys,
} from '@src/utils/protocolParams';
import React, { useMemo } from 'react';

export default function ProtocolParameterChange({
  governanceAction,
}: {
  governanceAction: IGovernanceAction;
}) {
  const { meta } = governanceAction;
  const { protocolParams } = meta;
  const epochParams = useAppSelector(selectProtocolParams) || {};

  const mappedArraysToObjectsProtocolParams = useMemo(
    () =>
      mapArrayToObjectByKeys(protocolParams as Record<string, unknown> | null, [
        'PlutusV1',
        'PlutusV2',
        'PlutusV3',
      ]),
    [protocolParams]
  );

  const updatableProtocolParams = useMemo(
    () =>
      filterUpdatableProtocolParams(
        epochParams,
        mappedArraysToObjectsProtocolParams,
        ['id', 'registered_tx_id', 'key']
      ),
    [epochParams, mappedArraysToObjectsProtocolParams]
  );

  const nonNullProtocolParams = useMemo(
    () =>
      filterOutNullParams(mappedArraysToObjectsProtocolParams, [
        'id',
        'registered_tx_id',
        'key',
      ]),
    [updatableProtocolParams, mappedArraysToObjectsProtocolParams]
  );

  if (!nonNullProtocolParams && !updatableProtocolParams) return null;

  return (
    <React.Fragment>
      <div className="rounded-lg border bg-dark-neutral-600 dark:border-dark-neutral-700">
        <table className="label12 w-full rounded-lg dark:text-white">
          <thead>
            <tr className="bg-dark-neutral-800 font-semibold">
              <th className="w-fit rounded-tl-lg">LABEL</th>
              <th className="!max-w-[261px] !bg-semantic-success-500">
                PROPOSED
              </th>
              <th className="!max-w-[261px] rounded-tr-lg !bg-semantic-error-500">
                EXISTING
              </th>
            </tr>
          </thead>
          <tbody>
            {nonNullProtocolParams &&
              Object.keys(nonNullProtocolParams).map((key) => {
                return (
                  <tr key={key}>
                    <td className="border-r border-dark-neutral-400 font-medium dark:text-white">
                      {key}
                    </td>
                    <td className="break-all border-r border-dark-neutral-400 align-top font-medium dark:text-white">
                      {JSON.stringify(nonNullProtocolParams[key])}
                    </td>
                    <td className="break-all align-top font-medium dark:text-white">
                      {updatableProtocolParams
                        ? JSON.stringify(updatableProtocolParams[key])
                        : ''}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}
